import { Card, compareUpNext, mergeTranslations } from '@lingoping/core';
import { StorageManager } from '../storage/index.js';
import { laterThan } from '../utils/timestamps.js';
import { AccessLockedError, isAccessLocked } from './access.js';
import { ensureCardImage } from './cardImageService.js';
import {
  PendingMeta,
  buildPendingCard,
  forgetPendingCards,
  isPendingCard,
  readPendingMeta,
  recordCouldntAdd,
  rememberPendingCard,
  sourceDeckFor,
} from './pendingCardStore.js';
import { createVocabularyCards } from './wordCardService.js';

/**
 * Lazy preparation (Docs/plans/CLOUD_DECKS_PLAN.md). A word the learner chose whose card could not be
 * made on the spot (offline, a server error, the daily limit of 100 newly prepared words) is a
 * pending card. This fills pending cards in: when the dashboard or a review window opens, and from
 * the service worker's 15-minute alarm. Any device can do it - a pending card syncs like any
 * other, and the text comes from the shared word library, so it is the same wherever it is made.
 *
 * - A word that was prepared (by this learner or anyone) is cheap; a new one is generated once.
 * - Failures keep the card pending. A daily-limit failure waits for the next UTC day; other
 *   failures are tried again at the next opportunity, but not more than once a minute.
 * - While the learner has no access (a 402 from the server, services/access.ts) nothing is tried:
 *   the cards stay pending, with no failed try counted, until the sync says access is back.
 * - A word that turns out not to be German is removed, and the learner is told once
 *   (`takeCouldntAdd`). A word that is already a card some other way is removed silently.
 */

/** Pending cards handled per run; the first ones in "Up next" order, the rest wait for the next run. */
export const MAX_FILLED_PER_RUN = 40;
/** The least time between two tries of the same card, unless the caller forces it. */
export const MIN_RETRY_GAP_MS = 60_000;

export interface FillResult {
  attempted: number;
  /** Cards that now have their text. */
  filled: number;
  /** Words dropped because they are not German. */
  removed: number;
  /** Cards still pending. */
  stillPending: number;
}

export interface FillOptions {
  /** Most cards to try in this run (default MAX_FILLED_PER_RUN). */
  max?: number;
  /** Ignore the one-minute gap (and the daily-limit wait). */
  force?: boolean;
  /** Called with each card after its text was saved. */
  onFilled?: (card: Card) => void;
  now?: Date;
}

const utcDay = (iso: string) => iso.slice(0, 10);

function due(meta: PendingMeta | undefined, now: Date, force?: boolean): boolean {
  if (!meta?.lastAttemptAt || force) return true;
  if (meta.code === 'daily_limit_reached') {
    return utcDay(meta.lastAttemptAt) !== utcDay(now.toISOString());
  }
  return now.getTime() - new Date(meta.lastAttemptAt).getTime() >= MIN_RETRY_GAP_MS;
}

/** Puts freshly prepared text on a pending card, keeping its place in the list and its id. */
export function fillCardText(pending: Card, prepared: Card): Card {
  return {
    ...pending,
    target_language: prepared.target_language,
    lemma: prepared.lemma,
    front_text: prepared.front_text,
    back_text: prepared.back_text,
    part_of_speech: prepared.part_of_speech,
    phonetic_ipa: prepared.phonetic_ipa,
    linguistic_meta: prepared.linguistic_meta,
    visual_meta: prepared.visual_meta,
    image_url: prepared.image_url,
    example_target: prepared.example_target,
    example_native: prepared.example_native,
    translations: mergeTranslations(pending.translations, prepared.translations),
    content_status: 'ready',
    updated_at: laterThan(pending.updated_at),
  };
}

let running: Promise<FillResult> | null = null;

/**
 * Fills pending cards in. Runs one at a time (a second call while one is running shares it) and
 * never throws.
 */
export function fillPendingCards(options: FillOptions = {}): Promise<FillResult> {
  if (running) return running;
  running = runFill(options)
    .catch((err): FillResult => {
      console.warn('[LingoPing] Preparing pending cards failed:', (err as Error).message);
      return { attempted: 0, filled: 0, removed: 0, stillPending: 0 };
    })
    .finally(() => {
      running = null;
    });
  return running;
}

async function runFill(options: FillOptions): Promise<FillResult> {
  const result: FillResult = { attempted: 0, filled: 0, removed: 0, stillPending: 0 };
  const userId = await StorageManager.getCurrentUserId();
  if (!userId) return result;
  const now = options.now ?? new Date();

  await migrateLegacyPendingWords(userId);

  const pending = (await StorageManager.getCards()).filter(isPendingCard).sort(compareUpNext);
  result.stillPending = pending.length;
  if (pending.length === 0) return result;
  if (await isAccessLocked()) return result;

  const meta = await readPendingMeta();
  const targets = pending
    .filter((c) => due(meta[c.id], now, options.force))
    .slice(0, options.max ?? MAX_FILLED_PER_RUN);
  if (targets.length === 0) return result;
  result.attempted = targets.length;

  // One request per language (always German today), 20 words per server call.
  const byLanguage = new Map<string, Card[]>();
  for (const card of targets) {
    const language = card.target_language || 'de';
    byLanguage.set(language, [...(byLanguage.get(language) ?? []), card]);
  }

  for (const [language, cards] of byLanguage) {
    let results: Awaited<ReturnType<typeof createVocabularyCards>>;
    try {
      results = await createVocabularyCards(
        cards.map((c) => ({
          word: c.lemma ?? c.front_text,
          context: meta[c.id]?.context,
          hint: meta[c.id]?.hint,
          cardId: c.id,
          // A card waiting as one meaning of its word is made as that meaning.
          senseKey: c.sense_key || c.sense_hint ? (c.sense_key ?? '') : undefined,
        })),
        userId,
        language
      );
    } catch (error) {
      if (error instanceof AccessLockedError) return result;
      const reason = (error as Error)?.message || 'Could not create the card.';
      results = cards.map((c) => ({
        word: c.lemma ?? c.front_text,
        error: reason,
        code: 'other' as const,
      }));
    }

    for (const [i, card] of cards.entries()) {
      const outcome = results[i];
      // Re-read: the learner may have removed the card, or another device may have filled it.
      const current = (await StorageManager.getCards()).find((c) => c.id === card.id);
      if (!current || !isPendingCard(current)) {
        await forgetPendingCards([card.id]);
        continue;
      }
      if (outcome?.card) {
        // The text and picture only: the card keeps its place in "Up next".
        const filled =
          (await StorageManager.updateCard(fillCardText(current, outcome.card))) ?? current;
        await forgetPendingCards([card.id]);
        void ensureCardImage(filled);
        result.filled++;
        result.stillPending--;
        options.onFilled?.(filled);
      } else if (outcome?.duplicate) {
        // Became a card some other way meanwhile (or its dictionary form was one already).
        await StorageManager.deleteCard(card.id);
        await forgetPendingCards([card.id]);
        result.stillPending--;
      } else if (outcome?.code === 'not_a_german_word' || outcome?.code === 'invalid_word') {
        // Not German, or not a usable word: it can never be prepared.
        await StorageManager.deleteCard(card.id);
        await forgetPendingCards([card.id]);
        await recordCouldntAdd(
          card.lemma ?? card.front_text,
          outcome.error ?? "That doesn't look like a German word."
        );
        result.removed++;
        result.stillPending--;
      } else {
        await rememberPendingCard(card.id, {
          attempts: (meta[card.id]?.attempts ?? 0) + 1,
          lastAttemptAt: now.toISOString(),
          code: outcome?.code ?? 'other',
        });
      }
    }
  }
  return result;
}

// ---------------------------------------------------------------------------------------------
// The old "words waiting for a card" list
// ---------------------------------------------------------------------------------------------

interface LegacyPendingWord {
  term: string;
  context?: string;
  hint?: string;
  deckId?: string;
  queuedAt?: string;
}

const LEGACY_KEY = 'lingoping_pending_words';
const MIGRATED_KEY = 'lingoping_pending_words_migrated_v1';

/**
 * Words that waited in the old local list become pending cards, once per user, keeping their
 * place in "Up next" (and the deck they were chosen in). Afterwards the old list is gone.
 */
export async function migrateLegacyPendingWords(userId: string): Promise<number> {
  const migratedKey = `${MIGRATED_KEY}_${userId}`;
  if (await StorageManager.getLocalValue<boolean>(migratedKey)) return 0;

  const legacyKey = `${LEGACY_KEY}_${userId}`;
  const words = (await StorageManager.getLocalValue<LegacyPendingWord[]>(legacyKey)) ?? [];
  const have = new Set(
    (await StorageManager.getCards()).map((c) => (c.lemma ?? c.front_text).trim().toLowerCase())
  );
  let created = 0;
  // The old list was newest first; the oldest choice goes first in Up next either way (queuedAt).
  for (const word of words) {
    const term = word.term?.trim();
    if (!term || have.has(term.toLowerCase())) continue;
    have.add(term.toLowerCase());
    const card = buildPendingCard({
      lemma: term,
      userId,
      source: word.deckId ? { kind: 'deck', deckId: word.deckId } : { kind: 'user' },
      queuedAt: word.queuedAt,
    });
    if (word.deckId) card.source_deck_id = await sourceDeckFor(word.deckId);
    await StorageManager.addCard(card);
    await rememberPendingCard(card.id, { context: word.context, hint: word.hint });
    created++;
  }
  await StorageManager.setLocalValue(legacyKey, null);
  await StorageManager.setLocalValue(migratedKey, true);
  return created;
}
